import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Category, GamesFilter } from '@/api/games';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { getSliderParams } from '@/helpers/getSliderParams';
import { useResponsive } from '@/modules/client';
import { createGamesSlides } from '@/modules/lobby/helpers';
import { useGetCommonStore } from '@/store';
import type { SliderParams } from '../components/LobbyGamesSlider';
import type { LobbyGamesSlideProps } from '../components/LobbyGamesSlider/components/Slide/Slide';

const CATEGORY_DATA_FALLBACK = {
  data: [],
  pagination: {
    currentPage: 1,
    nextPage: 1,
    prevPage: null,
    totalPages: 0,
    totalCount: 0,
  },
};

export const useGamesSliderData = (
  categoryId: Category,
  config: SliderParams
) => {
  const useCommonStore = useGetCommonStore();

  const { gamesByCategory } = useCommonStore(
    useShallow((state) => ({
      gamesByCategory: state.gamesByCategory,
    }))
  );

  const { currentMediaType } = useResponsive();

  // Get device-specific configuration
  const deviceConfig = useMemo(
    () => getSliderParams(config, currentMediaType),
    [config, currentMediaType]
  );

  // Extract category data with safe fallbacks
  const categoryData = useMemo<GamesFilter.Response>(
    () => gamesByCategory?.[categoryId] || CATEGORY_DATA_FALLBACK,
    [gamesByCategory, categoryId]
  );

  // Process games into slides
  const slides = useMemo<LobbyGamesSlideProps[]>(() => {
    const { data } = categoryData;

    if (!data || data.length === 0) return [];

    const games = mapGameV2ToGameV1(data);
    return createGamesSlides(games, deviceConfig.cols, deviceConfig.rows);
  }, [categoryData, deviceConfig.cols, deviceConfig.rows]);

  return {
    slides,
    pagination: categoryData.pagination,
    deviceConfig,
    isEmpty: !categoryData.data || categoryData.data.length === 0,
  };
};
